import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components } from '@/types/api.generated';
import { toApiListParams, type ListRequestParams } from './list-request';
import { CUSTOMER_FILTERS } from './filters.generated';

// The `/api/customers` filter whitelist is codegen from the backend
// `customerListConfig.filterable` via the OpenAPI `x-filterable` extension
// (issue #1407). Re-exported here so the `@/services/customers` import path
// is unchanged; edit the backend config, not this file.
export { CUSTOMER_FILTERS };

/**
 * Nested customer detail wire shape returned by `GET /api/customers/{id}`
 * (customer-routes `customerDetailSchema`): the customer row plus a
 * capped inline recent-orders preview and `orders_truncated`.
 */
export type CustomerDetail = components['schemas']['CustomerDetail'];

/**
 * US-CUSTOMER-002: inline-orders preview shape rendered by the
 * customer-detail recent-orders table. Kept hand-written: the generated
 * contract exposes the preview only as an ANONYMOUS inline schema on
 * `CustomerDetail['orders']` (no named component to alias — single-source
 * gap), and the resource's detail bridge additionally remaps the wire
 * `shopify_id` to `shopify_order_id` for the card's link target.
 */
export interface CustomerOrderSummary {
  id: string;
  shopify_order_id: string;
  name: string;
  order_number: string;
  total_price: string;
  financial_status: string | null;
  fulfillment_status: string | null;
  created_at: string;
}

/**
 * Customer row type = the generated OpenAPI component (ADR-0006 route-is-
 * spec; #1405 type-chain closure) — a backend field rename now fails `tsc`
 * instead of silently rendering `undefined`. The two extra properties are
 * NOT a hand-written re-widening: they ARE on the wire (nested inside
 * `CustomerDetail`) and are flattened onto the row by the customer
 * resource's detail bridge; they stay optional because list rows lack them.
 */
export type Customer = components['schemas']['Customer'] & {
  orders?: CustomerOrderSummary[];
  orders_truncated?: boolean;
};

export const customerAPI = {
  async getCustomers(params?: ListRequestParams): Promise<ListPage<Customer>> {
    return http.get<ListPage<Customer>>('/api/customers', {
      params: toApiListParams(params ?? {}, CUSTOMER_FILTERS),
    });
  },

  async getCustomer(id: string): Promise<CustomerDetail> {
    return http.get<CustomerDetail>(`/api/customers/${id}`);
  },
};
